import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getDrops, getEstimatedRates } from '@/lib/api';
import Navbar from '@/components/Navbar';
import DropCard from '@/components/DropCard';
import { Calendar as CalIcon, AlertTriangle, TrendingUp } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';

export default function DropCalendarPage() {
  const [drops, setDrops] = useState([]);
  const [estimates, setEstimates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const load = async () => {
      try {
        const [dRes, eRes] = await Promise.all([getDrops(), getEstimatedRates()]);
        setDrops(dRes.data);
        setEstimates(eRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const dropDates = drops.map(d => new Date(d.release_date));

  const liveDrops = drops.filter(d => d.status === 'live');
  const upcomingDrops = drops.filter(d => d.status === 'upcoming').sort((a, b) => new Date(a.release_date) - new Date(b.release_date));
  const pastDrops = drops.filter(d => d.status === 'ended');

  return (
    <div className="min-h-screen bg-background" data-testid="drop-calendar-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3 mb-2">
            <CalIcon className="w-6 h-6 text-primary" />
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight uppercase">Drop Calendar</h1>
          </div>
          <p className="text-muted-foreground mb-8">Never miss a release. Mark your calendar.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar Widget */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="bg-card border border-border/40 rounded-sm p-4" data-testid="calendar-widget">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                modifiers={{ dropDay: dropDates }}
                modifiersStyles={{ dropDay: { color: '#D4AF37', fontWeight: 'bold' } }}
                className="rounded-sm"
              />
              <div className="mt-4 space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-widest">Legend</p>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-xs text-muted-foreground">Drop Date</span>
                </div>
              </div>
            </div>

            {/* Estimated Rates for Drop Dates */}
            {estimates && (
              <div className="bg-card border border-border/40 rounded-sm p-4 mt-4" data-testid="calendar-estimates">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <h3 className="font-display font-bold text-sm">Est. Metal Rates</h3>
                </div>
                <div className="space-y-2">
                  {estimates.estimates.slice(0, 4).map(e => (
                    <div key={e.date} className="flex items-center justify-between text-xs bg-secondary/50 p-2 rounded-sm">
                      <span className="font-mono text-muted-foreground">{e.date_label}</span>
                      <div className="flex gap-3">
                        <span className="text-primary">Au {e.gold_estimated.toLocaleString()}</span>
                        <span className="text-silver">Ag {e.silver_estimated.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-start gap-2 mt-3 p-2 bg-yellow-500/5 border border-yellow-500/15 rounded-sm">
                  <AlertTriangle className="w-3 h-3 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-yellow-200/60 leading-relaxed">
                    Estimates only. Actual rates may vary significantly based on market conditions, geopolitics, and demand. Not financial advice.
                  </p>
                </div>
              </div>
            )}
          </motion.div>

          {/* Drops List */}
          <div className="lg:col-span-2 space-y-8">
            {liveDrops.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Badge className="bg-accent text-accent-foreground font-bold text-xs rounded-sm uppercase">LIVE</Badge>
                  <h2 className="font-display text-2xl font-bold">Active Drops</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {liveDrops.map((d, i) => <DropCard key={d.drop_id} drop={d} index={i} />)}
                </div>
              </div>
            )}

            {upcomingDrops.length > 0 && (
              <div>
                <h2 className="font-display text-2xl font-bold mb-4">Upcoming</h2>
                <div className="space-y-4">
                  {upcomingDrops.map((drop, i) => (
                    <motion.div
                      key={drop.drop_id}
                      className="flex items-center gap-4 bg-card border border-border/40 hover:border-primary/30 rounded-sm p-4 transition-colors duration-300"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.08 }}
                      data-testid={`upcoming-drop-${drop.drop_id}`}
                    >
                      <img src={drop.image_url} alt={drop.name} className="w-16 h-16 object-cover rounded-sm flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-bold truncate">{drop.name}</h3>
                        <p className="text-sm text-muted-foreground truncate">{drop.description}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Release</p>
                        <p className="font-mono text-sm text-primary">
                          {new Date(drop.release_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center h-40">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
